import { useCreation } from '@euler/utils/hooks/useCreation';
import { Audio, AVPlaybackStatus } from 'expo-av';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { BehaviorSubject } from 'rxjs';
import { clipUri } from '../voice/clipUri';

/** A voice to hear back: its audio, base64, as the player takes it. */
export interface TraineeReplayAudio {
  mime: 'audio/mpeg' | 'audio/wav';
  audio: string;
}

export interface TraineeReplay<K> {
  /** The line being heard back; null when none is. */
  playing$: BehaviorSubject<K | null>;
  /**
   * Plays a line's voice, or stops it when it is the one playing. `load`
   * fetches the audio; null when there is none to play.
   */
  toggle: (key: K, load: () => Promise<TraineeReplayAudio | null>) => void;
  stop: () => void;
}

/**
 * The trainee's own spoken lines, heard back: from the device in the scene,
 * from storage in the transcript. One plays at a time; `onStart` quiets the
 * customer first, and the host stops this when the customer speaks.
 */
export function useTraineeReplay<K>(onStart: () => void): TraineeReplay<K> {
  const playing$ = useCreation(() => new BehaviorSubject<K | null>(null), []);
  const current = useRef<{ sound: Audio.Sound; dispose: () => void } | null>(
    null,
  );
  // Bumped on every play and stop: a voice still loading may be stale.
  const generation = useRef(0);
  const started = useRef(onStart);
  started.current = onStart;

  const stop = useCallback(() => {
    generation.current += 1;
    playing$.next(null);
    const playing = current.current;
    current.current = null;
    if (!playing) return;
    void playing.sound
      .unloadAsync()
      .catch(error => console.error('trainee replay unload failed', error))
      .finally(playing.dispose);
  }, [playing$]);

  const toggle = useCallback(
    (key: K, load: () => Promise<TraineeReplayAudio | null>) => {
      const same = playing$.getValue() === key;
      stop();
      if (same) return;
      started.current();
      const mine = generation.current;
      playing$.next(key);
      void (async () => {
        try {
          const voice = await load();
          if (mine !== generation.current) return;
          if (!voice) {
            stop();
            return;
          }
          const file = await clipUri(
            `trainee-${Date.now()}`,
            voice.mime,
            voice.audio,
          );
          if (mine !== generation.current) {
            file.dispose();
            return;
          }
          const { sound } = await Audio.Sound.createAsync(
            { uri: file.uri },
            { shouldPlay: true },
            (status: AVPlaybackStatus) => {
              if (mine !== generation.current) return;
              if (status.isLoaded ? status.didJustFinish : !!status.error) {
                if (status.isLoaded === false)
                  console.error('trainee replay failed', status.error);
                stop();
              }
            },
          );
          if (mine !== generation.current) {
            // Stopped, or so short it finished while loading.
            void sound.unloadAsync().finally(file.dispose);
            return;
          }
          current.current = { sound, dispose: file.dispose };
        } catch (error) {
          console.error('trainee replay failed', error);
          if (mine === generation.current) stop();
        }
      })();
    },
    [playing$, stop],
  );

  useEffect(() => stop, [stop]);

  return useMemo(() => ({ playing$, toggle, stop }), [playing$, toggle, stop]);
}
